import { getV1CompaniesCompanyIdJobsId } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { useCallback } from 'react';

import { Job } from '~hooks/useJob/models';
import { LocationHistoryMinimal } from '~hooks/useLocationHistory/models';
import { getLocationHistoriesByOrderId } from '~hooks/useLocationHistory/useLocationHistory';
import connection from '~services/connectionModule';
import { useStores } from '~store';

export interface OrderReplayData {
  jobs: Job[];
  pingsByJobId: Map<string, LocationHistoryMinimal[]>;
}

/**
 * Loads everything Truck Replay needs for an order:
 * 1. every GPS ping for the order (cursor-followed by the location-history fn),
 *    grouped by `jobId`;
 * 2. the full Job for each distinct ping `jobId`.
 *
 * The truck set is derived from the ping `jobId`s, NOT from the dispatch store's
 * `orderJobs` — that list is dispatch-filtered and can drop trucks. The store is
 * read only as a cache; any `jobId` it misses is fetched individually so every
 * truck that produced pings is guaranteed to resolve.
 *
 * The consumer owns loading/progress state.
 */
export default function useOrderReplayData() {
  const { ordersDispatchStore } = useStores();

  const fetchReplayData = useCallback(
    async (
      companyId: string,
      orderId: string,
      signal?: AbortSignal,
    ): Promise<OrderReplayData> => {
      try {
        const pings = await getLocationHistoriesByOrderId(companyId, orderId, signal);

        const pingsByJobId = new Map<string, LocationHistoryMinimal[]>();
        for (const ping of pings) {
          const { jobId } = ping;
          if (!jobId) continue;

          const existing = pingsByJobId.get(jobId);
          if (existing) {
            existing.push(ping);
          } else {
            pingsByJobId.set(jobId, [ping]);
          }
        }

        const cachedById = new Map(
          (ordersDispatchStore.orderJobs.get(orderId) ?? []).map((job) => [job.id, job]),
        );

        const resolvedJobs = await Promise.all(
          [...pingsByJobId.keys()].map((jobId) =>
            resolveJob(companyId, jobId, cachedById.get(jobId), signal),
          ),
        );

        const jobs = resolvedJobs.filter((job): job is Job => job instanceof Job);

        return { jobs, pingsByJobId };
      } catch (error) {
        if (signal?.aborted) {
          throw error;
        }

        // Surface the failure noisily (toast + Sentry) at the hook boundary, then
        // re-throw so the consumer can show its error state.
        try {
          connection.handleRequestError(
            error,
            t('error_messages.truck_replay.failed_to_fetch'),
          );
        } catch {
          // handleRequestError always throws after surfacing the error.
        }

        throw error;
      }
    },
    [ordersDispatchStore],
  );

  return { fetchReplayData };
}

/**
 * Returns the cached job if present, otherwise fetches it by id. A per-job fetch
 * failure resolves to `null` (the truck is skipped) rather than failing the whole
 * replay — the error is still surfaced via the standard toast + Sentry path.
 */
async function resolveJob(
  companyId: string,
  jobId: string,
  cached: Job | undefined,
  signal?: AbortSignal,
): Promise<Job | null> {
  if (cached) return cached;

  try {
    const response = await getV1CompaniesCompanyIdJobsId({
      path: { 'company-id': companyId, id: jobId },
      signal,
    });

    return Job.parse(response.data.data);
  } catch (error) {
    if (signal?.aborted) {
      throw error;
    }

    try {
      connection.handleRequestError(
        error,
        t('error_messages.jobs.failed_to_fetch_individual_job'),
      );
    } catch {
      // handleRequestError always throws after surfacing the error; swallow so a
      // single truck's failure doesn't abort the rest of the replay.
    }

    return null;
  }
}
